import { apiFetch } from "./apiClient";

/** One selectable size on the guest's menu — deliberately narrower than the
 * admin's variant DTO. A diner picks a size and sees a price. */
export interface RoomServiceMenuVariantDto {
  /** The id to quote back when ordering. */
  roomRestaurantMenuVariantId: number;
  variantName: string;
  unit: string | null;
  portion: string | null;
  /** Already resolved — this size's own price, or the item's. */
  price: number;
  serviceChargeAmount: number;
  gstAmount: number;
  totalPrice: number;
  preparationTimeMinutes: number;
  /** Pre-select this one. At most one variant per item carries it. */
  isDefault: boolean;
  /** False greys the size out rather than hiding it — same treatment the item
   * itself gets. */
  isAvailable: boolean;
}

export interface RoomServiceMenuItemDto {
  roomRestaurantMenuId: number;
  itemName: string | null;
  description: string | null;
  foodType: string | null;
  price: number;
  serviceChargePercentage: number;
  gstPercentage?: number;
  preparationTimeMinutes?: number;
  isAvailable?: boolean;
  /** EMPTY means the item is ordered directly at `price` — the normal case.
   * Non-empty means the guest MUST pick one, and the order endpoint refuses a
   * line that doesn't. */
  variants?: RoomServiceMenuVariantDto[] | null;
}

export interface RoomServiceMenuCategoryDto {
  /** The reusable KitchenCategory master's id — Breakfast, Lunch, Bakery.
   * Replaced foodCategoryId in the 2026-09-12 kitchen change; catering keeps
   * FoodCategory and this menu no longer touches it. */
  kitchenCategoryId: number;
  categoryName: string | null;
  description: string | null;
  displayOrder: number;
  /** Which kitchen serves this section. Present for the staff order pad,
   * which groups a ticket by kitchen; the GUEST app should not render it —
   * a diner doesn't care which stove their dosa came off. */
  kitchenId: number;
  kitchenName: string | null;
  items: RoomServiceMenuItemDto[] | null;
}

/** The menu as one booking/room sees it — `canOrder` is false (with a
 * reason) when e.g. the stay hasn't started, room service is closed, or the
 * booking has no active room. */
export interface RoomServiceMenuDto {
  directBookingId: number;
  roomBookingId: number | null;
  branchId: number;
  roomNo: string | null;
  canOrder: boolean;
  cannotOrderReason: string | null;
  availableFoodTypes: string[] | null;
  categories: RoomServiceMenuCategoryDto[] | null;
}

export interface CreateRoomRestaurantOrderLineDto {
  roomRestaurantMenuId: number;
  /** WHICH SIZE. Required when the item has any orderable variant, rejected
   * when it has none — so a cart is never ambiguous about what it is buying.
   * Null is the normal case and the only one that existed before sizes. */
  roomRestaurantMenuVariantId?: number | null;
  quantity: number;
  notes: string | null;
}

export interface CreateRoomRestaurantOrderDto {
  directBookingId: number;
  roomBookingId: number | null;
  items: CreateRoomRestaurantOrderLineDto[] | null;
  paymentType: string | null;
  specialInstructions: string | null;
  isUrgent: boolean;
  createdBy: number | null;
  /** Required when called from the no-login guest app (no staff token) — the
   * backend checks this against the booking's checked-in guest phone in
   * place of a token. Ignored for an authenticated (staff) caller. */
  guestPhone: string | null;
}

export interface UpdateRoomRestaurantOrderStatusDto {
  orderStatus: string | null;
  remarks: string | null;
  performedBy: number | null;
}

export interface CancelRoomRestaurantOrderDto {
  cancellationReason: string | null;
  performedBy: number | null;
}

export interface CancelRoomRestaurantOrderLineDto {
  reason: string | null;
  performedBy: number | null;
}

export interface RoomRestaurantOrderDetailDto {
  orderDetailId: number;
  roomRestaurantMenuId: number | null;
  itemName: string | null;
  categoryName: string | null;
  /** The kitchen responsible for this line, snapshotted when the order was
   * placed — so re-filing or deleting the dish later can't rewrite which
   * kitchen an existing order says cooked it. Null on orders placed before
   * kitchens existed. */
  kitchenId: number | null;
  kitchenName: string | null;
  foodType: string | null;
  quantity: number;
  unitPrice: number;
  serviceChargeId: number | null;
  serviceChargePercentage: number;
  serviceChargeAmount: number;
  gstCategoryId: number | null;
  cgstPercentage: number;
  cgstAmount: number;
  sgstPercentage: number;
  sgstAmount: number;
  igstPercentage: number;
  igstAmount: number;
  gstPercentage: number;
  gstAmount: number;
  totalAmount: number;
  preparationTimeMinutes: number;
  /** Per-line status — a single dish can be cancelled without cancelling
   * the whole order (POST .../items/{orderDetailId}/cancel). */
  status: string | null;
  notes: string | null;
}

export interface RoomRestaurantOrderStatusHistoryDto {
  historyId: number;
  oldStatus: string | null;
  newStatus: string | null;
  changedOn: string;
  changedBy: number | null;
  source: string | null;
  remarks: string | null;
}

/** Maps to the backend's RoomRestaurantOrderDto — the real, live
 * RoomRestaurantOrders resource (an in-room dining order tied to a
 * DirectBooking/RoomBooking). Money fields are computed server-side. */
export interface RoomRestaurantOrderDto {
  orderId: number;
  orderNumber: string | null;
  accountId: number;
  branchId: number;
  directBookingId: number;
  roomBookingId: number | null;
  roomId: number;
  roomNo: string | null;
  roomTypeId: number;
  roomTypeName: string | null;
  buildingId: number | null;
  customerId: number | null;
  guestName: string | null;
  orderDate: string;
  orderStatus: string | null;
  paymentType: string | null;
  subTotal: number;
  serviceChargeAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  gstAmount: number;
  totalAmount: number;
  estimatedPreparationTimeMinutes: number;
  estimatedReadyDateTime: string | null;
  postedToFolio: boolean;
  invoiceId: number | null;
  postedToFolioOn: string | null;
  specialInstructions: string | null;
  isUrgent: boolean;
  cancelledOn: string | null;
  cancellationReason: string | null;
  createdOn: string;
  modifiedOn: string | null;
  items: RoomRestaurantOrderDetailDto[] | null;
  statusHistory: RoomRestaurantOrderStatusHistoryDto[] | null;
}

export interface RoomRestaurantOrderListFilters {
  accountId?: number;
  branchId?: number | null;
  directBookingId?: number;
  roomBookingId?: number;
  orderStatus?: string;
  fromDate?: string;
  toDate?: string;
}

const buildQuery = (params: object): string => {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value !== undefined && value !== null) qs.set(key, String(value));
  }
  const str = qs.toString();
  return str ? `?${str}` : "";
};

/** The menu as one booking sees it — guest-ordering surface. Requires
 * knowing the booking's directBookingId/roomBookingId already (there is no
 * anonymous "look up my stay by room number" endpoint). */
export const getRoomServiceMenu = async (
  directBookingId: number,
  roomBookingId: number | null,
  foodType?: string
): Promise<RoomServiceMenuDto> => {
  const res = await apiFetch(
    `/roomrestaurantorders/menu${buildQuery({ directBookingId, roomBookingId, foodType })}`
  );
  return res.json();
};

export const createRoomRestaurantOrder = async (
  payload: CreateRoomRestaurantOrderDto,
  // The no-login guest app must pass true: its order is authorized by
  // payload.guestPhone against the booking, never by whatever staff token
  // happens to be sitting in localStorage from an unrelated tab/device
  // (which the backend would otherwise treat as authoritative and reject
  // as cross-account, since a real token wins that check). A staff-side
  // caller placing an order on a guest's behalf should leave this false so
  // their own token/role (e.g. for a Complimentary order) still applies.
  { asAnonymousGuest = false }: { asAnonymousGuest?: boolean } = {}
): Promise<RoomRestaurantOrderDto> => {
  const res = await apiFetch(
    "/roomrestaurantorders",
    { method: "POST", body: JSON.stringify(payload) },
    { skipAuth: asAnonymousGuest }
  );
  return res.json();
};

/** Admin view — every order, filterable; newest first. */
export const listRoomRestaurantOrders = async (
  filters: RoomRestaurantOrderListFilters = {}
): Promise<RoomRestaurantOrderDto[]> => {
  const res = await apiFetch(`/roomrestaurantorders${buildQuery(filters)}`);
  return res.json();
};

/** One page of the Orders tab, filtered and counted by the server. */
export interface RoomRestaurantOrderPageDto {
  items: RoomRestaurantOrderDto[];
  total: number;
  page: number;
  pageSize: number;
  allCount: number;
  openCount: number;
  urgentOpenCount: number;
  revenue: number;
  statuses: string[];
}

export const listRoomRestaurantOrdersPage = async (q: {
  accountId?: number;
  branchId?: number | null;
  orderStatus?: string;
  search?: string;
  openOnly?: boolean;
  page: number;
  pageSize: number;
}): Promise<RoomRestaurantOrderPageDto> => {
  const res = await apiFetch(
    `/roomrestaurantorders/page${buildQuery({
      accountId: q.accountId,
      branchId: q.branchId ?? undefined,
      orderStatus: q.orderStatus || undefined,
      search: q.search?.trim() || undefined,
      openOnly: q.openOnly ? true : undefined,
      page: q.page,
      pageSize: q.pageSize,
    })}`
  );
  return res.json();
};

/** Kitchen view — open work only, urgent first. */
export const listRoomRestaurantOrderQueue = async (
  accountId?: number,
  branchId?: number | null
): Promise<RoomRestaurantOrderDto[]> => {
  const res = await apiFetch(
    `/roomrestaurantorders/queue${buildQuery({ accountId, branchId })}`
  );
  return res.json();
};

export const getRoomRestaurantOrder = async (
  id: number,
  // Pass the caller's own accountId when viewing another tenant's dashboard
  // as partner admin — the token alone can't be trusted for the ownership
  // check in that case (see the controller's GetById comment).
  accountId?: number
): Promise<RoomRestaurantOrderDto> => {
  const res = await apiFetch(
    `/roomrestaurantorders/${id}${buildQuery({ accountId })}`
  );
  return res.json();
};

/** Kitchen — Pending -> SentToKitchen -> OutForDelivery -> Delivered, exactly
 * as defined server-side in RoomRestaurantOrderStatus (Domain/Entities/
 * RoomRestaurantOrder.cs); Cancelled/Rejected are terminal and reachable from
 * any non-terminal state. */
export const updateRoomRestaurantOrderStatus = async (
  id: number,
  payload: UpdateRoomRestaurantOrderStatusDto,
  // Same partner-admin-impersonation reasoning as getRoomRestaurantOrder.
  accountId?: number
): Promise<RoomRestaurantOrderDto> => {
  const res = await apiFetch(
    `/roomrestaurantorders/${id}/status${buildQuery({ accountId })}`,
    { method: "PATCH", body: JSON.stringify(payload) }
  );
  return res.json();
};

/** Cancel the whole order. */
export const cancelRoomRestaurantOrder = async (
  id: number,
  payload: CancelRoomRestaurantOrderDto,
  accountId?: number
): Promise<RoomRestaurantOrderDto> => {
  const res = await apiFetch(
    `/roomrestaurantorders/${id}/cancel${buildQuery({ accountId })}`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  return res.json();
};

/** Cancel one line — the soup is off, the rest of the order still goes out. */
export const cancelRoomRestaurantOrderLine = async (
  id: number,
  orderDetailId: number,
  payload: CancelRoomRestaurantOrderLineDto,
  accountId?: number
): Promise<RoomRestaurantOrderDto> => {
  const res = await apiFetch(
    `/roomrestaurantorders/${id}/items/${orderDetailId}/cancel${buildQuery({ accountId })}`,
    { method: "POST", body: JSON.stringify(payload) }
  );
  return res.json();
};

/** Total charged to a room booking so far — a plain decimal. Includes
 * already-posted orders (see the controller's remarks), so this is a
 * reference total for staff to review, not something to add on top of an
 * invoice that may already include it. */
export const getRoomRestaurantOrderFolioTotal = async (
  roomBookingId: number,
  // Same partner-admin-impersonation reasoning as getRoomRestaurantOrder.
  accountId?: number
): Promise<number> => {
  const res = await apiFetch(
    `/roomrestaurantorders/folio/${roomBookingId}${buildQuery({ accountId })}`
  );
  return res.json();
};

// --------------------------------------------------------- KITCHEN ROUTING

/** One line on a kitchen's ticket. */
export interface KitchenOrderLineDto {
  orderDetailId: number;
  itemName: string | null;
  /** The menu item's short name, printed on the KOT instead of itemName. */
  shortName?: string | null;
  /** The size sold: Half, Full, 500 g. Null for an item without sizes. */
  variantName?: string | null;
  /** The kitchen category, snapshotted — "South Indian", "Beverages". */
  categoryName: string | null;
  foodType: string | null;
  quantity: number;
  preparationTimeMinutes: number;
  /** Per-line note ("extra spicy"), distinct from the order's
   * specialInstructions which apply to the whole tray. */
  notes: string | null;
}

/** One kitchen's share of one order — the printable ticket. */
export interface KitchenOrderTicketDto {
  /** Null for lines placed before kitchens existed, or whose menu item was
   * already deleted when the migration ran. Those group under "Unassigned"
   * rather than vanishing — a line nobody can route still has to appear on
   * somebody's screen. */
  kitchenId: number | null;
  kitchenName: string;
  kitchenCode: string | null;
  /** The slowest item in THIS kitchen's share. Per kitchen, not per order:
   * telling the bakery the order needs 25 minutes because the biriyani does
   * is noise on their ticket. */
  preparationTimeMinutes: number;
  items: KitchenOrderLineDto[] | null;
}

/** An order split into the ticket each kitchen has to work — the KOT view.
 * Built from the kitchen snapshot on each line, not a live join, so an
 * existing order's tickets never change when a dish is re-filed. */
export interface KitchenOrderRoutingDto {
  orderId: number;
  orderNumber: string;
  roomId: number | null;
  roomNo: string | null;
  guestName: string | null;
  orderDate: string;
  orderStatus: string;
  /** Repeated onto every ticket, not just the header — a runner handing three
   * tickets to three kitchens needs each to say on its own face that it's
   * urgent. */
  isUrgent: boolean;
  specialInstructions: string | null;
  kitchens: KitchenOrderTicketDto[] | null;
}

/** Kitchen-wise routing for one order — cancelled lines are already excluded
 * server-side, so what comes back is exactly what has to be cooked. */
export const getRoomRestaurantOrderKitchenRouting = async (
  id: number,
  accountId?: number
): Promise<KitchenOrderRoutingDto> => {
  const res = await apiFetch(
    `/roomrestaurantorders/${id}/kitchen-routing${buildQuery({ accountId })}`
  );
  return res.json();
};
